import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import { Character } from '../objects/Character';
import { Occupation } from '../objects/Occupation';
import { HollandCode } from "../objects/HollandCode";
import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserGamePad } from "src/games/shared/Gamepad";
import { PhaserModal, PhaserModalConfig, ModalOption } from "src/games/shared/Modal";
import { PhaserButtonConfig, PhaserButton } from "src/games/shared/Button";
import initializeInteractiveCursor from "src/games/shared/InteractiveCursorUtil";
import TranslateApi from "src/utils/TranslateHelper";
import { Color } from "src/games/shared/ColorValue";
import AudioTranslations from "src/constants/AudioTranslations";

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    score: Phaser.GameObjects.Text;
    scoreBorder: Phaser.GameObjects.Image;
    player: Phaser.Physics.Arcade.Sprite;
    characters: Character[];
    collisionLayer: Phaser.Tilemaps.ObjectLayer;
    characterLayer: Phaser.Tilemaps.ObjectLayer;
    borders: Phaser.Tilemaps.StaticTilemapLayer;
    doubleJump: boolean = true;
    possibleOccupations: Set<Occupation>;
    astronaut: Phaser.Physics.Arcade.Image;
    gamePad: PhaserGamePad;
    characterModal: PhaserModal;
    astronautModal: PhaserModal;
    frameCount: number = 0;
    hollandCodeGraphic: Phaser.GameObjects.Image;

    xVel: number = 0;
    yVel: number = 0;

    private JUMPING_VELOCITY = 400;
    private WALKING_VELOCITY = 200;

    private buttonConfig: PhaserButtonConfig = {
        backgroundColor: Color.KUDER_SECONDARY_BUTTON,
        smartSize: true
    };

    private modalConfig: PhaserModalConfig = {
        title: "",
        buttonWidth: 220,
        buttonConfig: this.buttonConfig,
    };

    private translateString: string = "JobMatching.Phaser.";

    constructor() {
        super({
            key: "MainScene"
        });
    }

    preload(): void {
        this.load.image('drawtiles', require("/src/assets/qci/tilemaps/tiles/drawtiles.png"));
        this.load.image('background', require("/src/assets/qci/backgrounds/grade5-S__ bg.jpg"));
        this.load.tilemapTiledJSON('map', require("/src/assets/qci/maps/job_matching.json"));
        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.atlas("pilots", require("/src/assets/qci/atlas/starPilotAll.png"), require("/src/assets/qci/atlas/starPilotAll.json"));
        this.load.atlas("aliens", require("/src/assets/qci/atlas/smallAliens.png"), require("/src/assets/qci/atlas/smallAliens.json"));
        this.load.spritesheet('player', require('src/assets/qci/sprites/studentPilot/student_spritemap-42x64.png'), { frameWidth: 42, frameHeight: 64 });
        this.load.atlas("aliens", require("/src/assets/qci/atlas/gameUniversity.png"), require("/src/assets/qci/atlas/gameUniversity.json"));
        this.load.image('help', require("/src/assets/qci/sprites/help_icon.png"));
        this.load.image('hollandCodes', require("/src/assets/qci/sprites/hollandCodes.png"));

        PhaserGamePad.preload(this);
        PhaserButton.preload(this);
        PhaserModal.preload(this);

        var keys =[
            "JobMatching.Phaser.npc.A",
            "JobMatching.Phaser.npc.C",
            "JobMatching.Phaser.npc.E",
            "JobMatching.Phaser.npc.I",
            "JobMatching.Phaser.npc.R",
            "JobMatching.Phaser.npc.S",
        ];

        keys.forEach((key) => {
            this.load.audio(key, AudioTranslations.FindByKeyAndLanguage(key, TranslateApi.currentLanguage.code).url);
        });
    }

    initialize(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.characters = [];
        this.initializeMap();
    }

    create(): void {
        bindAllOfThis(this, MainScene.prototype);
        this.initialize();
        this.generateScene();
        this.initializeGame();
        this.setBorderCollisions();
        initializeInteractiveCursor(this);
    }

    /**
     * Initializes the tilemap and sets tile collision properties
     * */
    initializeMap(): void {
        this.map = this.make.tilemap({ key: 'map' });
        var backgroundTiles = this.map.addTilesetImage('background');
        backgroundTiles.setTileSize(16, 16);
        this.map.createDynamicLayer('Background Layer', backgroundTiles, 0, 0);

        this.collisionLayer = this.map.getObjectLayer('Collisions');
        this.characterLayer = this.map.getObjectLayer('Object Layer');
    }

    setBorderCollisions(): void {
        this.map.getObjectLayer("Borders").objects.forEach((object: TiledGameObject) => {
            // The group will have an array of objects - these are the individual collision shapes
            var gameObject = new Phaser.GameObjects.Zone(this, object.x, object.y, object.width, object.height);
            gameObject.type = object.type;
            this.physics.add.existing(gameObject, true);
            this.physics.add.collider(this.player, gameObject);
        });
    }

    initializeGame(): void {
        // Reset players
        this.characters.forEach((c: Character) => {
            c.enabled = true;
            c.label = null;
            c.assignedOccupation = null;
        });
        // Reinitialize possible occupations
        this.possibleOccupations = new Set(Occupation.OCCUPATIONS);
    }

    generateScene() {
        this.generateNPCs();
        this.generatePlayer();
        this.setCollisions();
        this.createGamePad();

        // Setup the camera to follow the player
        this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
        this.cameras.main.startFollow(this.player);

        // Initialize score
        var scoreText = TranslateApi.translate(this.translateString + "score") + ": ";
        this.score = this.add.text(0, 0, scoreText + 0, { backgroundColor: '#000000', fontSize: 20, padding: 24, fontFamily: 'Russo One, Arial' });
        this.score.setTint(0xffffff);
        this.score.setDataEnabled();
        this.score.setData('score', 0);
        this.score.setDepth(4);
        this.score.setScrollFactor(0); // Make it stay on screen when camera changes
        this.score.setPosition(this.gameWidth - this.score.width - 60, 50);

        // Border around score
        this.scoreBorder = this.add.image(this.score.x - 5, this.score.y - 5, "pixel");
        this.scoreBorder.setOrigin(0);
        this.scoreBorder.setDepth(1);
        this.scoreBorder.setDisplaySize(this.score.displayWidth + 10, this.score.displayHeight + 10);
        this.scoreBorder.setTint(0xff0700);
        this.scoreBorder.setScrollFactor(0);

        var helpIcon = this.add.image(0, 0, "help");
        helpIcon.setOrigin(0);
        helpIcon.setDisplaySize(30, 30);
        helpIcon.setScrollFactor(0);
        helpIcon.setName('help');
        helpIcon.setPosition(this.gameWidth - 45, 15);
        helpIcon.setInteractive();

        this.hollandCodeGraphic = this.add.image(0, 0, "hollandCodes");
        this.hollandCodeGraphic.setOrigin(0);
        this.hollandCodeGraphic.setScrollFactor(0);
        this.hollandCodeGraphic.setVisible(false);
        this.hollandCodeGraphic.setScale(0.5);
        this.hollandCodeGraphic.setPosition(this.gameWidth / 2 - this.hollandCodeGraphic.displayWidth / 2, this.gameHeight / 2 - this.hollandCodeGraphic.displayHeight / 2);

        helpIcon.on('pointerdown', () => {
            this.hollandCodeGraphic.setVisible(!this.hollandCodeGraphic.visible);

            if (this.hollandCodeGraphic.visible) {
                this.children.bringToTop(this.hollandCodeGraphic);
                this.sound.stopAll();
                // Close any modals on screen and disable the player so  that focus is on the help image
                this.closeOnScreenModals();
                this.player.disableBody();
            }
            else {
                this.children.moveDown(this.hollandCodeGraphic);
                this.player.enableBody(true, this.player.x, this.player.y, true, true);
            }
        });
    }

    /**
     * Generates the player with animations
     * */
    generatePlayer(): void {
        this.player = this.physics.add.sprite(600, 220, "player");
        this.player.setBounce(0.2);
        this.player.setFrame(4);
        // For collision bounds
        this.player.setSize(30, 48);
        this.player.setOffset(8, 15);
        this.player.setDisplaySize(32, 48);

        //  Player animations, turning, walking left and walking right.
        this.anims.create({
            key: 'left',
            frames: this.anims.generateFrameNumbers('player', { start: 0, end: 3 }),
            frameRate: 10,
            repeat: -1
        });

        this.anims.create({
            key: 'turn',
            frames: [{ key: 'player', frame: 4 }],
            frameRate: 20
        });

        this.anims.create({
            key: 'right',
            frames: this.anims.generateFrameNumbers('player', { start: 5, end: 8 }),
            frameRate: 10,
            repeat: -1
        });

        // Player collides with the borders
        this.physics.add.collider(this.player, this.borders);
    }

    /**
     * Generates the astronaut and 10 characters with their Holland Codes
     */
    generateNPCs(): void {
        this.characters = [];
        let characterCodes = [
            HollandCode.R,
            HollandCode.I,
            HollandCode.I,
            HollandCode.A,
            HollandCode.A,
            HollandCode.S,
            HollandCode.S,
            HollandCode.S,
            HollandCode.E,
            HollandCode.C,
        ];

        // Shuffle them
        characterCodes = characterCodes.sort(() => .5 - Math.random());

        var i = 0;
        this.characterLayer.objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "astronaut") {
                this.astronaut = this.physics.add.sprite(ob.x, ob.y, "pilots", "s");
                this.astronaut.setDisplaySize(32, 48);
                this.astronaut.setDataEnabled();
                this.astronaut.setData('enabled', true);
                this.physics.add.collider(this.astronaut, this.borders);
                this.physics.add.overlap(this.astronaut, this.player);
            }
            else {
                let char = this.physics.add.sprite(ob.x, ob.y + 30, "aliens", ob.type);
                char.setDisplaySize(40, 38);
                this.physics.add.collider(char, this.borders);
                this.physics.add.overlap(char, this.player);
                this.characters.push(new Character(ob.id, characterCodes[i], char));
                i++;
            }
        });
    }

    setCollisions(): void {
        this.collisionLayer.objects.forEach((object: TiledGameObject) => {
            // The group will have an array of objects - these are the individual collision shapes
            var gameObject = new Phaser.GameObjects.Zone(this, object.x, object.y, object.width, object.height);
            gameObject.type = object.type;
            this.physics.add.existing(gameObject, true);
            this.physics.add.collider(this.player, gameObject, (player, object: any) => {
                if (object.type === "canJumpThrough") {
                    // Allow player to jump up through these platforms
                    object.body.checkCollision.down = false;
                }
            });
            this.physics.add.collider(this.astronaut, gameObject);
            this.characters.forEach((c: Character) => {
                this.physics.add.collider(c.object, gameObject);
            });
        });
    }

    /**
     * Called when meeting the astronaut, checks if the game is over or not
     * */
    talkToAstronaut(): void {
        this.astronaut.setData('enabled', false);
        if (!this.isModalOnScreen()) {
            var text, buttonText, id, positive1, positive2;
            var nextToAstro = true;

            let config = this.modalConfig;
            config.buttonWidth = 100;

            // Check if the game is over yet
            if (this.isGameOver()) { 
                text = TranslateApi.translate(this.translateString + "astronaut.complete");
                buttonText = TranslateApi.translate("common.done");
                id = 1;
                nextToAstro = false;
            }
            else {
                // Check if they have assigned all characters, otherwise game isn't over because score < 20
                var numNotAssigned = 0;
                this.characters.forEach((c) => {
                    if (c.assignedOccupation == null) {
                        numNotAssigned++;
                    }
                });
                if (numNotAssigned === 1) {
                    positive1 = TranslateApi.translate(this.translateString + "astronaut.keepGoing1_singular");
                    positive2 = TranslateApi.translate(this.translateString + "astronaut.keepGoing2_singular");
                    text = positive1 + numNotAssigned + positive2;
                }
                else if (numNotAssigned > 1) {
                    positive1 = TranslateApi.translate(this.translateString + "astronaut.keepGoing1_plural");
                    positive2 = TranslateApi.translate(this.translateString + "astronaut.keepGoing2_plural");
                    text = positive1 + numNotAssigned + positive2;
                }
                else {
                    // They have assigned all but their score is < 20, so tell them to try different matches
                    text = TranslateApi.translate(this.translateString + "astronaut.wrongAnswers");
                }
                buttonText = TranslateApi.translate("common.ok");
                id = 2;
            }
            config.title = text;
            this.astronautModal = new PhaserModal(this, 0, 0, [{ displayText: buttonText, id: id }], config);
            this.astronautModal.depth = 1001;
            if (nextToAstro) {
                this.astronautModal.setPosition(this.astronaut.getBottomRight().x - this.astronautModal.width / 2, this.gameHeight - 300);
            }
            else {
                this.astronautModal.setPosition(this.astronaut.getBottomRight().x - this.astronautModal.width / 2, this.gameHeight - 400);
            }

            this.astronautModal.on('optionClicked', (op: ModalOption) => {
                if (op.id === 2) {
                    // Just continue on with the game
                    this.astronautModal.destroy();
                }
                else {
                    this.player.disableBody();
                    // Game Over
                    (this.sys.game as any).onComplete();
                }
            });
        }
    }

    getKeyPressKey(index: number): string {
        let key: string;
        index++;
        if (index <= 9) {
            key = index.toString();
        } else if (index === 10) {
            key = "0";
        }

        return key;
    }

    /**
     * Called when player meets a character, asks user for occupation assignment
     * */
    talkToCharacter(character): void {
        if (!this.isModalOnScreen()) {
            character.enabled = false;
            var text = TranslateApi.translate(this.translateString + "npc." + character.hollandCode.actualCode);
            var audio = this.sound.add(this.translateString + "npc." + character.hollandCode.actualCode);
            audio.play();

            var options = [];
            var id = 0;
            this.possibleOccupations.forEach((occ: Occupation) => {
                //var translatedJob = this.getKeyPressKey(id) + ': ' + TranslateApi.translate(occ.title);
                var translatedJob = TranslateApi.translate(occ.title);
                let option = { displayText: translatedJob, id: id, object: occ };
                options.push(option);
                id++;
            });

            // Last option is always for None/Unassign
            //var none = "DEL : " + TranslateApi.translate(this.translateString + "none");
            var none = TranslateApi.translate(this.translateString + "none");
            options.push({ displayText: none, id: id, object: null });

            var config = this.modalConfig;
            config.title = text;
            config.buttonWidth = 220;

            this.characterModal = new PhaserModal(this, 0, 0, options, config);
            // On top of gamepad!
            this.characterModal.depth = 1001;
            this.moveModal(character);
            this.characterModal.on('optionClicked', (op: ModalOption) => {
                // Assign character new occupation (op.object)
                this.updateScoreboard(character, op.object);
                this.showAssignedOccupation(character);
                this.characterModal.destroy();
            });
        }
    }

    /**
     * Updates score based on the character/occupation
     */
    updateScoreboard(character: Character, newOccupation: Occupation) {
        let currentScore = this.score.getData('score');
        var assignmentPoints;
        var characterCode = character.hollandCode;

        // Unassigning Character (None)
        if (newOccupation == null) {
            // Get their previous assignment points
            if (character.assignedOccupation != null) {
                let assignedOccupationCode = character.assignedOccupation.hollandCode;
                let prevPoints = this.getAssignmentPoints(characterCode, assignedOccupationCode);
                // Subtract those points from current score
                currentScore -= prevPoints;
                // Add their previous assigned occupation back to the list
                this.possibleOccupations.add(character.assignedOccupation);
                character.assignedOccupation = null;
                // Update Score
                this.changeScore(currentScore);
            }
            return;
        }

        // Assigning Character a real occupation
        var occupationCode = newOccupation.hollandCode;
        let isAlreadyAssigned = character.assignedOccupation != null;

        if (!isAlreadyAssigned) {
            // First time assigning this character
            assignmentPoints = this.getAssignmentPoints(characterCode, occupationCode);
        }
        else {
            // Get their previous assignment points
            let assignedOccupationCode = character.assignedOccupation.hollandCode;
            let prevPoints = this.getAssignmentPoints(characterCode, assignedOccupationCode);
            // Subtract those points from current score
            currentScore -= prevPoints;
            // Calculate new points
            assignmentPoints = this.getAssignmentPoints(characterCode, occupationCode);
            // Add their previous assigned occupation back to the list
            this.possibleOccupations.add(character.assignedOccupation);
        }

        // Reassign
        character.assignedOccupation = newOccupation;
        // Remove this occupation from list of possible occupations
        this.possibleOccupations.delete(newOccupation);

        // Finally, update Score
        let newScore = currentScore + assignmentPoints;
        this.changeScore(newScore);
    }

    changeScore(newScore: number): void {
        var color, tweenX;
        tweenX = this.score.x;
        if (newScore < this.score.getData("score")) {
            color = Color.COLORS.get("red");
            tweenX += 5;
        }
        else if (newScore > this.score.getData("score")) {
            color = Color.COLORS.get("green");
        }
        this.tweens.add({
            targets: this.score,
            duration: 50,
            repeat: 4,
            yoyo: true,
            ease: "Linear",
            x: tweenX,
            onStart: () => {
                this.score.setTint(color);
            },
            onComplete: () => {
                this.score.clearTint();
            }
        });

        var scoreText = TranslateApi.translate(this.translateString + "score") + ": ";
        this.score.setData('score', newScore);
        this.score.setText(scoreText + newScore);
        this.scoreBorder.setDisplaySize(this.score.displayWidth + 10, this.score.displayHeight + 10);
    }

    update(): void {
        this.updatePlayerMovement();
        this.checkIfPlayerIsNextToAstronaut();
        this.updateModalVisibility();
    }

    checkIfPlayerIsNextToAstronaut(): void {
        var area = this.astronaut.getBounds();
        var rect = new Phaser.Geom.Rectangle(area.x, area.y, 75, 50);
        rect.centerX = this.astronaut.getCenter().x;
        rect.centerY = this.astronaut.getCenter().y;
        if (Phaser.Geom.Intersects.RectangleToRectangle(rect, this.player.getBounds()) && this.astronaut.getData('enabled')) {
            this.talkToAstronaut();
        }

        if (!Phaser.Geom.Intersects.RectangleToRectangle(rect, this.player.getBounds()) && !this.astronaut.getData('enabled')) {
            // Once they leave the astronaut, set it as enabled again so they can keep checking the game state/end the game
            this.astronaut.setData('enabled', true);
            if (this.astronautModal != null && this.isModalOnScreen()) {
                this.astronautModal.destroy();
                this.sound.stopAll();
            }
        }
    }

    /**
     * Closes modals if player walks away from them
     */
    updateModalVisibility(): void {
        var c: Character;
        for (c of this.characters) {
            var rect = c.object.getBounds();
            rect.width = 100;
            rect.height = 100;
            rect.centerX = c.object.getCenter().x;
            rect.centerY = c.object.getCenter().y;
            // Player is next to a candidate so just return (modal should be open)
            if (Phaser.Geom.Intersects.RectangleToRectangle(rect, this.player.getBounds())) {
                return;
            }
        }

        // Player isn't next to any candidate, so close the modal
        if (this.isModalOnScreen() && this.characterModal != null) {
            this.characterModal.destroy();
            this.sound.stopAll();
        }

    }

    isGameOver(): boolean {
        // Are all characters assigned to an occupation?
        var c: Character;
        for (c of this.characters) {
            if (c.assignedOccupation == null) {
                return false;
            }
        }

        // Have to have at least 20 points to end game
        if (this.score.getData('score') >= 20) {
            return true;
        }
    }

    /**
     * Show the assigned occupation as a label next to the character
     * */
    showAssignedOccupation(character: Character): void {
        // No label to show, they chose "None"
        if (character.assignedOccupation == null) {
            // Delete the current label 
            if (character.label != null) {
                character.label.destroy();
                character.label = null;
            }
            return;
        }

        // Remove their existing label if they have one
        if (character.label) {
            character.label.destroy();
            character.label = null;
        }

        var x = character.object.x - 10;
        if (character.object.x > this.gameWidth - 30) {
            x = character.object.x - 6 * character.assignedOccupation.title.length;
        }
        var label = TranslateApi.translate(character.assignedOccupation.title);
        var labelStyle = { fontSize: 15, fontFamily: 'Russo One, Arial', padding: 10, align: 'center', backgroundColor: '#000000', color: '#ffffff' };
        character.label = this.add.text(0, 0, label, labelStyle);
        character.label.x = character.object.getCenter().x - character.label.width / 2;
        if (character.label.x < 100) {
            character.label.x = character.object.x;
        }
        if (character.label.getBottomRight().x >= this.gameWidth - 10) {
            character.label.x = this.gameWidth - character.label.width - 10;
        }
        character.label.y = character.object.getTopLeft().y - character.label.height - 10;
        character.label.setAlpha(0.75);
    }

    // For debugging only
    getCharacterInfo(): void {
        this.characters.forEach((c: Character) => {
            console.log(c.assignedOccupation, c.label);
        });
    }

    /**
     * Finds the best possible matching occupation for the specified character (since there is not a 1:1 ratio of codes/occupations)
     */
    findBestMatch(character: Character): Occupation {
        var best = 0;
        var bestOcc = null;
        this.possibleOccupations.forEach((occ) => {
            let points = this.getAssignmentPoints(character.hollandCode, occ.hollandCode);
            if (points > best) {
                best = points;
                bestOcc = occ;
            }
        });
        return bestOcc;
    }

    /**
     * Get the correct number of points for the specified character holland code/occupation code assignment
     */
    getAssignmentPoints(characterCode: HollandCode, occupationCode: HollandCode): number {
        let matrix = [
            [3, 2, 1, 0, 1, 2],
            [2, 3, 2, 1, 0, 1],
            [1, 2, 3, 2, 1, 0],
            [0, 1, 2, 3, 2, 1],
            [1, 0, 1, 2, 3, 2],
            [2, 1, 0, 1, 2, 3]
        ];

        let code_index = ["R", "I", "A", "S", "E", "C"];
        return matrix[code_index.indexOf(characterCode.actualCode)][code_index.indexOf(occupationCode.actualCode)];
    }

    /**
     * Checks if there is already a modal open on screen (we don't want to be able to add more than one modal to a scene)
     */
    isModalOnScreen(): boolean {
        for (var c of this.children.list) {
            if (c.type === "PhaserModal") {
                return true;
            }
        }
        return false;
    }

    moveModal(character: Character): void {
        var x, y;
        var padding = character.object.displayWidth;
        var characterX = character.object.x - this.cameras.main.scrollX;
        // If running off the right, put modal on the left of the character
        if (characterX + padding + this.characterModal.width >= this.gameWidth - padding) {
            x = characterX - this.characterModal.width - padding;
        }
        else {
            // Otherwise, put it to the right of the character
            x = characterX + padding;
        }

        var characterY = character.object.getBottomRight().y - this.cameras.main.scrollY;
        // If running off the bottom or top, vertically center modal
        if ((characterY + this.characterModal.height / 2 >= this.gameHeight - padding) || (characterY - this.characterModal.height / 2 <= padding)) {
            y = this.gameHeight / 2 - this.characterModal.height / 2;
        }
        else {
            // Otherwise, put it next to the character
            y = characterY - this.characterModal.height / 2;
        }

        this.characterModal.setPosition(x, y);
    }

    closeOnScreenModals(): void {
        this.children.list.forEach((c) => {
            if (c.type === "PhaserModal") {
                var modal = c as PhaserModal;
                modal.destroy();
            }
        });
    }

    /**
     * GamePad/Player Movement 
     * EVENT BASED FOR JUMPING, UPDATE BASED FOR LEFT/RIGHT
     */
    createGamePad(): void {
        this.gamePad = new PhaserGamePad(this);
        this.gamePad.on("keydown_up", this.playerJump);
        this.gamePad.on("keydown_a", this.playerJump);
        this.gamePad.on("keydown_b", this.bDown);
    }

    moveLeft() {
        if (this.player.body.enable) {
            this.xVel += -this.WALKING_VELOCITY;
            this.player.anims.play('left', true);
        }
    }

    moveRight() {
        if (this.player.body.enable) {
            this.xVel += this.WALKING_VELOCITY;
            this.player.anims.play('right', true);
        }
    }

    bDown() {
        if (this.hollandCodeGraphic.visible) {
            return;
        }

        if (this.astronautModal && this.astronautModal.active) {
            this.astronautModal.selectLastOption();
            return;
        }

        for (var c of this.characters) {
            var area = c.object.getBounds();
            area.width = 150;
            area.height = 150;
            var rect = new Phaser.Geom.Rectangle(area.x, area.y, area.width, area.height);
            rect.centerX = c.object.getCenter().x;
            rect.centerY = c.object.getCenter().y;
            if (Phaser.Geom.Intersects.RectangleToRectangle(rect, this.player.getBounds())) {
                this.talkToCharacter(c);
            }
        }
    }

    playerJump() {
        if (this.player.body.onFloor()) {
            this.player.body.setVelocityY(-this.JUMPING_VELOCITY);
        } else if (this.doubleJump) {
            this.doubleJump = false;
            this.player.body.setVelocityY(-this.JUMPING_VELOCITY);
        }
    }

    updatePlayerMovement(): void {
        this.xVel = 0;

        if (this.gamePad.keys.left.isDown) {
            this.moveLeft();
        }
        if (this.gamePad.keys.right.isDown) {
            this.moveRight();
        }

        this.player.setVelocityX(this.xVel);

        if (this.xVel === 0) {
            this.player.anims.play('turn');
        }

        if (this.frameCount % 4 === 0) {
            if (this.player.body.onFloor()) {
                this.doubleJump = true;
            }
        }
        if (this.frameCount > 64000) {
            this.frameCount = 0;
        }
        this.frameCount++;
    }
}



// WEBPACK FOOTER //
// ./src/games/JobMatchingGame/scenes/MainScene.ts